import { get } from "aws-amplify/api";
import "containers/common/List.css";
import Moment from "moment";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { Link } from "react-router-dom";
import {
    createColumnHelper,
} from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

const columnHelper = createColumnHelper();

// ─── SubTable: standalone component so hooks can be used inside ───────────────
function SubTable({ propertyList }) {
    const createId = useMemo(() => createElementIdFactory("asic-search"), []);
    const subtableElementIds = useMemo(() => createReusableTableElementIds(createId, "subtable"), [createId]);
    
    const data = useMemo(() => propertyList || [], [propertyList]);

    const columns = useMemo(() => [
        columnHelper.accessor("property_key", {
            header: "Cityscope Ref",
            size: 100,
            cell: (info) => (
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`../properties/${info.getValue()}`}
                >
                    {info.getValue()}
                </a>
            ),
        }),
        columnHelper.accessor("classification", { header: "Classification", size: 250 }),
        columnHelper.accessor("owner_type",     { header: "Owner Type",     size: 150 }),
        columnHelper.accessor("area",           { header: "Area",           size: 150 }),
        columnHelper.accessor("sale_price",     { header: "Sale Price",     size: 150 }),
    ], []);

    return (
        <div style={{ padding: "20px" }}>
            <GenericTable
                columns={columns}
                data={data}
                section="asic-search-subtable"
                pageSize={PAGE_SIZES.asicSearchSubTable}
                enablePagination={true}
                hasHeaderSorting={true}
                elementIds={subtableElementIds}
            />
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function AsicSearchQueue({ isAuthenticated }) {

    // ── State ────────────────────────────────────────────────────────────────
    const [isLoading,    setIsLoading]    = useState(false);
    const [hasSearched,  setHasSearched]  = useState(false);
    const [asicSearches, setAsicSearches] = useState([]);
    const [rowCount,     setRowCount]     = useState(0);
    const [expanded,     setExpanded]     = useState({});

    const [cityscope,         setCityscope]         = useState(null);
    const [selectedCityscope, setSelectedCityscope] = useState(null);
    const [cityscopeOptions,  setCityscopeOptions]  = useState([]);

    const [searchOnOption,         setSearchOnOption]         = useState("next_search");
    const [selectedSearchOnOption, setSelectedSearchOnOption] = useState({ value: "next_search", label: "Next Search" });

    const [nextSearchDate,             setNextSearchDate]             = useState(new Date());
    const [nextSearchOperator,         setNextSearchOperator]         = useState("lessthan");
    const [selectedNextSearchOperator, setSelectedNextSearchOperator] = useState({ value: "lessthan", label: "less than" });

    const [lastSearchDate,             setLastSearchDate]             = useState(new Date());
    const [lastSearchOperator,         setLastSearchOperator]         = useState("lessthan");
    const [selectedLastSearchOperator, setSelectedLastSearchOperator] = useState({ value: "lessthan", label: "less than" });

    // Element IDs setup
    const createId = useMemo(() => createElementIdFactory("asic-search"), []);
    const mainTableElementIds = useMemo(() => createReusableTableElementIds(createId, "main"), [createId]);

    // Static options — never change, no need for state
    const operatorOptions = useMemo(() => [
        { value: "lessthan",    label: "less than"    },
        { value: "greaterthan", label: "greater than" },
        { value: "equal",       label: "equal"        },
    ], []);

    const searchOnOptions = useMemo(() => [
        { value: "next_search", label: "Next Search" },
        { value: "last_search", label: "Last Search" },
    ], []);

    const filterSelectStyles = useMemo(() => ({
        container: (base) => ({ ...base, width: "100%" }),
        control: (base) => ({ ...base, minHeight: 38 }),
    }), []);

    // ── API helpers ──────────────────────────────────────────────────────────
    const getAsicSearches = useCallback(async (filter) => {
        // Remove null/undefined keys so they are not sent as "null" strings
        const queryParams = Object.fromEntries(
            Object.entries(filter).filter(([, v]) => v !== null && v !== undefined)
        );
        try {
            const { body } = await get({
                apiName: "colin",
                path: "/queues/asicsearches",
                options: { queryParams },
            }).response;
            return await body.json();
        } catch (error) {
            console.error("getAsicSearches error:", error);
            alert(`${error}. Please refresh the page (F5) to try again.`);
            return [];
        }
    }, []);

    const getCityscopes = useCallback(async () => {
        try {
            const { body } = await get({
                apiName: "colin",
                path: "/cityscopes/dropdown",
            }).response;
            const data = await body.json();
            if (data) setCityscopeOptions(data);
        } catch (error) {
            console.error("Error getting cityscopes:", error);
        }
    }, []);

    // ── Initial load ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isAuthenticated) return;
        document.title = "ASIC Search Queue";

        getCityscopes();
        // Run only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleCityscopeChange = useCallback((selected) => {
        setSelectedCityscope(selected);
        setCityscope(selected?.value ?? null);
    }, []);

    const handleSearchOnOptionChange = useCallback((selected) => {
        setSelectedSearchOnOption(selected);
        setSearchOnOption(selected.value);

        if (selected.value === "next_search") {
            setNextSearchDate(new Date());
            setNextSearchOperator("lessthan");
            setSelectedNextSearchOperator({ value: "lessthan", label: "less than" });
            setLastSearchDate(null);
            setLastSearchOperator(null);
            setSelectedLastSearchOperator(null);
        } else {
            setLastSearchDate(new Date());
            setLastSearchOperator("lessthan");
            setSelectedLastSearchOperator({ value: "lessthan", label: "less than" });
            setNextSearchDate(null);
            setNextSearchOperator(null);
            setSelectedNextSearchOperator(null);
        }
    }, []);

    const handleNextSearchOperatorChange = useCallback((selected) => {
        setSelectedNextSearchOperator(selected);
        setNextSearchOperator(selected.value);
    }, []);

    const handleLastSearchOperatorChange = useCallback((selected) => {
        setSelectedLastSearchOperator(selected);
        setLastSearchOperator(selected.value);
    }, []);

    const handleNextDateChange = useCallback((date) => {
        setNextSearchDate(date ? new Date(date) : null);
    }, []);

    const handleLastDateChange = useCallback((date) => {
        setLastSearchDate(date ? new Date(date) : null);
    }, []);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        setHasSearched(true);
        setIsLoading(true);
        try {
            const filterOptions = {
                last_search: {
                    lastsearchoperator: lastSearchOperator,
                    datelastsearch: lastSearchDate ? Moment(lastSearchDate).format("YYYY-MM-DD") : null,
                },
                next_search: {
                    nextsearchoperator: nextSearchOperator,
                    datenextsearch: nextSearchDate ? Moment(nextSearchDate).format("YYYY-MM-DD") : null,
                },
            };

            const data = await getAsicSearches({
                cityscope,
                ...filterOptions[searchOnOption],
            });

            const results = data || [];
            setAsicSearches(results);
            setRowCount(results.flat().length);
        } catch (e) {
            alert(e);
        } finally {
            setIsLoading(false);
        }
    }, [cityscope, lastSearchDate, lastSearchOperator, nextSearchDate, nextSearchOperator, searchOnOption, getAsicSearches]);

    // ── Table columns ────────────────────────────────────────────────────────
    const formatter = useMemo(() => new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 0,
        currencyDisplay: "symbol",
    }), []);

    const columns = useMemo(() => [
        // Expander column
        columnHelper.display({
            id: "expander",
            size: 27,
            enableSorting: false,
            header: () => null,
            cell: ({ row }) =>
                row.original.property_list?.length > 0 ? (
                    <button
                        id={createId("main", "expand", "row", row.id)}
                        onClick={() => row.toggleExpanded()}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px" }}
                        aria-label="Expand row"
                    >
                        {row.getIsExpanded() ? "▼" : "▶"}
                    </button>
                ) : null,
    }),
    columnHelper.accessor("owner_id", {
        header: "Owner ID",
        size: 50,
        cell: (info) => (
            <Link id={`asic-search-main-link-owner-${info.getValue()}`} to={`/owners/${info.getValue()}`}>
                {info.getValue()}
            </Link>
        ),
    }),
    columnHelper.accessor("owner_name",   { header: "Name",         size: 200 }),
    columnHelper.accessor("acn",          { header: "ACN",          size: 65 }),
    columnHelper.accessor("search_notes", { header: "Search Notes", size: 150 }),
    columnHelper.accessor("dir_name_1",   { header: "Director 1",   size: 120 }),
    columnHelper.accessor("dir_name_2",   { header: "Director 2",   size: 120 }),
    columnHelper.accessor("date_last_search", {
        header: "Date Last Searched",
        size: 70,
        sortingFn: "datetime",
        cell: (info) => info.getValue()
            ? Moment(info.getValue()).local().format("DD-MM-YYYY")
            : null,
    }),
    columnHelper.accessor("date_next_search", {
        header: "Date Next Search",
        size: 70,
        sortingFn: "datetime",
        cell: (info) => info.getValue()
            ? Moment(info.getValue()).local().format("DD-MM-YYYY")
            : null,
    }),
    columnHelper.accessor(
        (row) => row.property_list
            ? row.property_list.filter((r) => r.owner_type === "Direct").length
            : null,
        { id: "direct_owned", header: "Direct Owned", size: 70 }
    ),
    columnHelper.accessor(
        (row) => row.property_list
            ? row.property_list
                .filter((r) => r.owner_type === "Direct")
                .map((r) => parseFloat((r.sale_price || "0").replace(/[^0-9.-]+/g, "")))
                .reduce((a, b) => a + b, 0)
            : null,
        {
            id: "sum_direct_owned",
            header: "Direct Owned $ Value",
            size: 85,
            cell: (info) => info.getValue() !== null ? formatter.format(info.getValue()) : null,
        }
    ),
    columnHelper.accessor(
        (row) => row.property_list
            ? row.property_list.filter((r) => r.owner_type === "Indirect").length
            : null,
        { id: "indirect_owned", header: "Indirect Owned", size: 85 }
    ),
    columnHelper.accessor(
        (row) => row.property_list
            ? row.property_list
                .filter((r) => r.owner_type === "Indirect")
                .map((r) => parseFloat((r.sale_price || "0").replace(/[^0-9.-]+/g, "")))
                .reduce((a, b) => a + b, 0)
            : null,
        {
            id: "sum_indirect_owned",
            header: "Indirect Owned $ Value",
            size: 85,
            cell: (info) => info.getValue() !== null ? formatter.format(info.getValue()) : null,
        }
    ),
], [createId, formatter]);

    // ── Render ───────────────────────────────────────────────────────────────
    if (!isAuthenticated) {
        return <div className="AgentList"><p>User not authenticated.</p></div>;
    }

    return (
        <div id="asic-search-queue-page" className="AgentList">
            <div id="asic-search-queue-content" className="agents">
                <form id="asic-search-filter-form" onSubmit={handleSubmit}>
                    <Row id="asic-search-filter-row" className="g-3 align-items-end">
                        <Col xs={12} sm={6} lg={2}>
                            <Form.Group controlId="cityscope">
                                <Form.Label id="asic-search-label-cityscope">Cityscope</Form.Label>
                                <Select
                                    inputId="asic-search-input-cityscope"
                                    instanceId="asic-search-instance-cityscope"
                                    value={selectedCityscope}
                                    onChange={handleCityscopeChange}
                                    options={cityscopeOptions}
                                    styles={filterSelectStyles}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={6} lg={2}>
                            <Form.Group controlId="searchOn">
                                <Form.Label id="asic-search-label-search-on">Search On</Form.Label>
                                <Select
                                    inputId="asic-search-input-search-on"
                                    instanceId="asic-search-instance-search-on"
                                    value={selectedSearchOnOption}
                                    onChange={handleSearchOnOptionChange}
                                    options={searchOnOptions}
                                    styles={filterSelectStyles}
                                />
                            </Form.Group>
                        </Col>

                        {searchOnOption === "next_search" && (
                            <>
                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId="selectNextSearchOperator">
                                        <Form.Label id="asic-search-label-next-operator">Search Type</Form.Label>
                                        <Select
                                            inputId="asic-search-input-next-operator"
                                            instanceId="asic-search-instance-next-operator"
                                            value={selectedNextSearchOperator}
                                            onChange={handleNextSearchOperatorChange}
                                            options={operatorOptions}
                                            styles={filterSelectStyles}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId="nextSearchDate">
                                        <Form.Label id="asic-search-label-next-date">Search Date</Form.Label>
                                        <DatePicker
                                            id="asic-search-input-next-date"
                                            selected={nextSearchDate}
                                            onChange={handleNextDateChange}
                                            dateFormat="dd/MM/yyyy"
                                            className="form-control custom-datepicker w-100"
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                            </>
                        )}

                        {searchOnOption === "last_search" && (
                            <>
                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId="selectLastSearchOperator">
                                        <Form.Label id="asic-search-label-last-operator">Search Type</Form.Label>
                                        <Select
                                            inputId="asic-search-input-last-operator"
                                            instanceId="asic-search-instance-last-operator"
                                            value={selectedLastSearchOperator}
                                            onChange={handleLastSearchOperatorChange}
                                            options={operatorOptions}
                                            styles={filterSelectStyles}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} lg={2}>
                                    <Form.Group controlId="lastSearchDate">
                                        <Form.Label id="asic-search-label-last-date">Search Date</Form.Label>
                                        <DatePicker
                                            id="asic-search-input-last-date"
                                            selected={lastSearchDate}
                                            onChange={handleLastDateChange}
                                            dateFormat="dd/MM/yyyy"
                                            className="form-control w-100"
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                            </>
                        )}

                        <Col xs={12} sm={6} lg={2}>
                            <Button id="asic-search-button-submit" type="submit" variant="primary" className="w-100">Search</Button>
                        </Col>
                    </Row>
                </form>

                {hasSearched && (
                <div id="asic-search-row-count" style={{ fontSize: "16px", margin: "10px 0px 10px 0px", color: rowCount >= 1000 ? "red" : "black", fontWeight: rowCount >= 1000 ? "bold" : "normal" }}>
                    Total Rows: {rowCount}
                    {rowCount >= 1000 && " — Limit reached, please refine your search"}
                </div>
                )}

                <Row id="asic-search-results-row" className="show-Container">
                    <Col sm={12}>
                        {isLoading ? (
                            <div id="asic-search-loading-container" className="outer-div">
                                <div id="asic-search-loading-spinner-wrapper" className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color="#e03c31"
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        ) : hasSearched ? (
                            <>
                                <GenericTable
                                    columns={columns}
                                    data={asicSearches}
                                    section="asic-search"
                                    pageSize={PAGE_SIZES.asicSearchResults}
                                    enablePagination={true}
                                    hasHeaderSorting={true}
                                    enableExpansion={true}
                                    expanded={expanded}
                                    onExpandedChange={setExpanded}
                                    renderExpandedRow={(row) => (
                                        <SubTable propertyList={row.original.property_list} />
                                    )}
                                    elementIds={mainTableElementIds}
                                />
                            </>
                        ) : null}
                    </Col>
                </Row>
            </div>
        </div>
    );
}

export default AsicSearchQueue;